'use client';
import React, { useState, useEffect, useCallback } from 'react';
import { createClient } from '@/lib/supabase/client';
import { toast, Toaster } from 'sonner';
import Icon from '@/components/ui/AppIcon';
import Breadcrumb from '@/components/Breadcrumb';
import ConfirmDialog from '@/components/ui/ConfirmDialog';

const supabase = createClient();

interface Licenza {
  id: string;
  nome_software: string;
  versione: string;
  produttore: string;
  tipo_licenza: string;
  numero_licenze_acquistate: number;
  numero_licenze_assegnate: number;
  data_scadenza: string | null;
  costo_totale: number;
  stato: string;
  note: string;
}

interface Ricambio {
  id: string;
  nome: string;
  codice: string;
  categoria: string;
  fornitore_nome: string;
  quantita_disponibile: number;
  scorta_minima: number;
  costo_unitario: number;
  posizione: string;
}

interface BudgetReparto {
  id: string;
  reparto: string;
  anno: number;
  budget_totale: number;
  speso: number;
  impegnato: number;
}

const licenzaStatoColor: Record<string, string> = {
  attiva: 'bg-green-500/20 text-green-400',
  in_scadenza: 'bg-amber-500/20 text-amber-400',
  scaduta: 'bg-red-500/20 text-red-400',
  sospesa: 'bg-gray-500/20 text-gray-400',
};

export default function AssetManagementContent() {
  const [tab, setTab] = useState<'licenze' | 'ricambi' | 'budget'>('licenze');
  const [licenze, setLicenze] = useState<Licenza[]>([]);
  const [ricambi, setRicambi] = useState<Ricambio[]>([]);
  const [budget, setBudget] = useState<BudgetReparto[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formLicenza, setFormLicenza] = useState({ nome_software: '', versione: '', produttore: '', tipo_licenza: '', numero_licenze_acquistate: 1, data_scadenza: '', costo_totale: 0 });
  const [formRicambio, setFormRicambio] = useState({ nome: '', codice: '', categoria: '', fornitore_nome: '', quantita_disponibile: 0, scorta_minima: 1, costo_unitario: 0, posizione: '' });
  const [formBudget, setFormBudget] = useState({ reparto: '', anno: new Date().getFullYear(), budget_totale: 0, speso: 0, impegnato: 0 });
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<{ open: boolean; id: string; name: string; table: string }>({ open: false, id: '', name: '', table: '' });

  const [editItem, setEditItem] = useState<{ type: 'licenza' | 'ricambio' | 'budget'; data: any } | null>(null);
  const [showEditForm, setShowEditForm] = useState(false);

  const openEdit = (type: 'licenza' | 'ricambio' | 'budget', data: any) => {
    setEditItem({ type, data });
    if (type === 'licenza') {
      setFormLicenza({ nome_software: data.nome_software, versione: data.versione || '', produttore: data.produttore || '', tipo_licenza: data.tipo_licenza || '', numero_licenze_acquistate: data.numero_licenze_acquistate, data_scadenza: data.data_scadenza || '', costo_totale: data.costo_totale || 0 });
    } else if (type === 'ricambio') {
      setFormRicambio({ nome: data.nome, codice: data.codice || '', categoria: data.categoria || '', fornitore_nome: data.fornitore_nome || '', quantita_disponibile: data.quantita_disponibile, scorta_minima: data.scorta_minima, costo_unitario: data.costo_unitario || 0, posizione: data.posizione || '' });
    } else {
      setFormBudget({ reparto: data.reparto, anno: data.anno, budget_totale: data.budget_totale, speso: data.speso || 0, impegnato: data.impegnato || 0 });
    }
    setShowEditForm(true);
  };

  const handleEdit = async () => {
    if (!editItem) return;
    setSaving(true);
    let error: any = null;
    if (editItem.type === 'licenza') {
      if (formLicenza.numero_licenze_acquistate <= 0) { toast.error('Numero licenze deve essere > 0'); setSaving(false); return; }
      ({ error } = await supabase.from('licenze_software').update(formLicenza).eq('id', editItem.data.id));
    } else if (editItem.type === 'ricambio') {
      if (formRicambio.quantita_disponibile < 0) { toast.error('Quantità non può essere negativa'); setSaving(false); return; }
      ({ error } = await supabase.from('ricambi').update(formRicambio).eq('id', editItem.data.id));
    } else {
      if (formBudget.budget_totale <= 0) { toast.error('Budget deve essere > 0'); setSaving(false); return; }
      ({ error } = await supabase.from('budget_reparti').update(formBudget).eq('id', editItem.data.id));
    }
    if (error) { toast.error('Errore: ' + error.message); } else { toast.success('Aggiornato con successo'); }
    setShowEditForm(false);
    setEditItem(null);
    fetchData();
    setSaving(false);
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    const [{ data: l }, { data: r }, { data: b }] = await Promise.all([
      supabase.from('licenze_software').select('*').order('nome_software'),
      supabase.from('ricambi').select('*').order('nome'),
      supabase.from('budget_reparti').select('*').order('reparto'),
    ]);
    setLicenze(l || []);
    setRicambi(r || []);
    setBudget(b || []);
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const creaLicenza = async () => {
    if (!formLicenza.nome_software.trim()) { toast.error('Nome software obbligatorio'); return; }
    if (formLicenza.numero_licenze_acquistate <= 0) { toast.error('Numero licenze deve essere maggiore di 0'); return; }
    if (formLicenza.costo_totale < 0) { toast.error('Costo non può essere negativo'); return; }
    if (formLicenza.data_scadenza && new Date(formLicenza.data_scadenza) < new Date()) {
      toast.error('Attenzione: la data di scadenza è già passata');
    }
    setSaving(true);
    const { error } = await supabase.from('licenze_software').insert({ ...formLicenza, numero_licenze_assegnate: 0, stato: 'attiva' });
    if (error) { toast.error('Errore: ' + error.message); } else { toast.success('Licenza creata'); }
    setShowForm(false);
    fetchData();
    setSaving(false);
  };

  const creaRicambio = async () => {
    if (!formRicambio.nome.trim()) { toast.error('Nome obbligatorio'); return; }
    if (formRicambio.quantita_disponibile < 0) { toast.error('Quantità non può essere negativa'); return; }
    if (formRicambio.costo_unitario < 0) { toast.error('Costo non può essere negativo'); return; }
    setSaving(true);
    const { error } = await supabase.from('ricambi').insert(formRicambio);
    if (error) { toast.error('Errore: ' + error.message); } else { toast.success('Ricambio creato'); }
    setShowForm(false);
    fetchData();
    setSaving(false);
  };

  const creaBudget = async () => {
    if (!formBudget.reparto.trim()) { toast.error('Reparto obbligatorio'); return; }
    if (formBudget.budget_totale <= 0) { toast.error('Budget deve essere maggiore di 0'); return; }
    setSaving(true);
    const { error } = await supabase.from('budget_reparti').insert(formBudget);
    if (error) { toast.error('Errore: ' + error.message); } else { toast.success('Budget creato'); }
    setShowForm(false);
    setFormBudget({ reparto: '', anno: new Date().getFullYear(), budget_totale: 0, speso: 0, impegnato: 0 });
    fetchData();
    setSaving(false);
  };

  const eliminaItem = async () => {
    try {
      const { error } = await supabase.from(deleteConfirm.table).delete().eq('id', deleteConfirm.id);
      if (error) throw error;
      toast.success('Elemento eliminato');
      setDeleteConfirm({ open: false, id: '', name: '', table: '' });
      fetchData();
    } catch (err: any) {
      toast.error('Errore eliminazione: ' + err.message);
    }
  };

  const aggiornaQuantita = async (id: string, delta: number, current: number) => {
    const nuova = Math.max(0, current + delta);
    await supabase.from('ricambi').update({ quantita_disponibile: nuova }).eq('id', id);
    fetchData();
  };

  const disponibili = (l: Licenza) => l.numero_licenze_acquistate - l.numero_licenze_assegnate;
  const percentualeUso = (l: Licenza) => l.numero_licenze_acquistate > 0 ? Math.round((l.numero_licenze_assegnate / l.numero_licenze_acquistate) * 100) : 0;

  return (
    <div className="min-h-full bg-background flex flex-col h-full">
      <Toaster position="bottom-right" theme="dark" richColors />

      <ConfirmDialog
        open={deleteConfirm.open}
        title="Elimina elemento"
        message={`Sei sicuro di voler eliminare "${deleteConfirm.name}"? Questa azione non può essere annullata.`}
        confirmLabel="Elimina"
        onConfirm={eliminaItem}
        onCancel={() => setDeleteConfirm({ open: false, id: '', name: '', table: '' })}
        danger
      />

      <Breadcrumb items={[{ label: 'Asset e Licenze' }]} />
      <div className="flex gap-1 mb-4 bg-surface-elevated rounded-xl p-1 w-fit">
        {(['licenze', 'ricambi', 'budget'] as const).map(t => (
          <button key={t} onClick={() => setTab(t)} className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${tab === t ? 'bg-sky-600 text-white' : 'text-muted-color hover:text-white'}`}>
            {t === 'licenze' ? 'Licenze Software' : t === 'ricambi' ? 'Ricambi e Materiali' : 'Budget Reparti'}
          </button>
        ))}
      </div>

      <div className="flex items-center justify-between mb-3">
        <p className="text-sm text-muted-color">
          {tab === 'licenze' ? `${licenze.length} licenze` : tab === 'ricambi' ? `${ricambi.length} ricambi` : `${budget.length} reparti`}
        </p>
        {tab !== 'budget' && (
          <button onClick={() => setShowForm(true)} className="flex items-center gap-2 bg-sky-600 hover:bg-sky-500 text-white px-4 py-2 rounded-lg text-sm font-semibold transition-colors">
            <Icon name="PlusIcon" size={16} /> {tab === 'licenze' ? 'Nuova Licenza' : 'Nuovo Ricambio'}
          </button>
        )}
        {tab === 'budget' && (
          <button onClick={() => setShowForm(true)} className="flex items-center gap-2 bg-sky-600 hover:bg-sky-500 text-white px-4 py-2 rounded-lg text-sm font-semibold transition-colors">
            <Icon name="PlusIcon" size={16} /> Nuovo Budget
          </button>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12"><div className="w-8 h-8 border-2 border-sky-500 border-t-transparent rounded-full animate-spin" /></div>
      ) : (
        <div className="flex-1 overflow-y-auto">
          {tab === 'licenze' && (
            <div className="space-y-3">
              {licenze.length === 0 ? <div className="text-center py-12 text-muted-color">Nessuna licenza</div> :
              licenze.map(l => (
                <div key={l.id} className="bg-surface-elevated border border-border rounded-xl p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`text-xs px-2 py-0.5 rounded-full ${licenzaStatoColor[l.stato] || ''}`}>{l.stato.replace(/_/g, ' ')}</span>
                        {disponibili(l) <= 0 && <span className="text-xs bg-red-500/20 text-red-400 px-2 py-0.5 rounded-full">Esaurite</span>}
                      </div>
                      <p className="text-sm font-semibold text-white">{l.nome_software} {l.versione && `v${l.versione}`}</p>
                      <p className="text-xs text-muted-color">{l.produttore} • {l.tipo_licenza}</p>
                      <div className="mt-2">
                        <div className="flex items-center justify-between text-xs mb-1">
                          <span className="text-muted-color">{l.numero_licenze_assegnate}/{l.numero_licenze_acquistate} assegnate</span>
                          <span className="text-white">{percentualeUso(l)}%</span>
                        </div>
                        <div className="w-full bg-surface rounded-full h-1.5">
                          <div className={`h-1.5 rounded-full ${percentualeUso(l) >= 100 ? 'bg-red-500' : percentualeUso(l) >= 80 ? 'bg-amber-500' : 'bg-sky-500'}`} style={{ width: `${Math.min(100, percentualeUso(l))}%` }} />
                        </div>
                      </div>
                    </div>
                    <div className="flex items-start gap-2">
                      <div className="text-right">
                        <p className="text-sm font-semibold text-white">€{l.costo_totale?.toLocaleString('it-IT')}</p>
                        {l.data_scadenza && <p className="text-xs text-muted-color">{new Date(l.data_scadenza).toLocaleDateString('it-IT')}</p>}
                        <p className={`text-xs font-semibold mt-1 ${disponibili(l) > 0 ? 'text-green-400' : 'text-red-400'}`}>{disponibili(l)} disponibili</p>
                      </div>
                      <button onClick={() => openEdit('licenza', l)} className="p-1.5 text-muted-color hover:text-sky-400 hover:bg-sky-500/10 rounded-lg transition-colors" title="Modifica">
                        <Icon name="PencilIcon" size={15} />
                      </button>
                      <button
                        onClick={() => setDeleteConfirm({ open: true, id: l.id, name: l.nome_software, table: 'licenze_software' })}
                        className="p-1.5 text-muted-color hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                        title="Elimina licenza"
                      >
                        <Icon name="TrashIcon" size={15} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {tab === 'ricambi' && (
            <div className="space-y-3">
              {ricambi.length === 0 ? <div className="text-center py-12 text-muted-color">Nessun ricambio</div> :
              ricambi.map(r => (
                <div key={r.id} className={`bg-surface-elevated border rounded-xl p-4 ${r.quantita_disponibile <= r.scorta_minima ? 'border-amber-500/40' : 'border-border'}`}>
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        {r.quantita_disponibile <= r.scorta_minima && <span className="text-xs bg-amber-500/20 text-amber-400 px-2 py-0.5 rounded-full">⚠️ Scorta bassa</span>}
                        {r.categoria && <span className="text-xs bg-surface text-muted-color px-2 py-0.5 rounded-full">{r.categoria}</span>}
                      </div>
                      <p className="text-sm font-semibold text-white">{r.nome}</p>
                      <div className="flex gap-3 text-xs text-muted-color mt-1">
                        {r.codice && <span>#{r.codice}</span>}
                        {r.fornitore_nome && <span>{r.fornitore_nome}</span>}
                        {r.posizione && <span>📦 {r.posizione}</span>}
                        {r.costo_unitario > 0 && <span>€{r.costo_unitario}/pz</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button onClick={() => aggiornaQuantita(r.id, -1, r.quantita_disponibile)} className="w-7 h-7 rounded-lg bg-surface hover:bg-surface-elevated border border-border text-white text-sm font-bold transition-colors">-</button>
                      <div className="text-center w-12">
                        <p className={`text-lg font-bold ${r.quantita_disponibile <= r.scorta_minima ? 'text-amber-400' : 'text-white'}`}>{r.quantita_disponibile}</p>
                        <p className="text-xs text-muted-color">min {r.scorta_minima}</p>
                      </div>
                      <button onClick={() => aggiornaQuantita(r.id, 1, r.quantita_disponibile)} className="w-7 h-7 rounded-lg bg-surface hover:bg-surface-elevated border border-border text-white text-sm font-bold transition-colors">+</button>
                      <button onClick={() => openEdit('ricambio', r)} className="p-1.5 text-muted-color hover:text-sky-400 hover:bg-sky-500/10 rounded-lg transition-colors ml-1" title="Modifica">
                        <Icon name="PencilIcon" size={15} />
                      </button>
                      <button
                        onClick={() => setDeleteConfirm({ open: true, id: r.id, name: r.nome, table: 'ricambi' })}
                        className="p-1.5 text-muted-color hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                        title="Elimina ricambio"
                      >
                        <Icon name="TrashIcon" size={15} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {tab === 'budget' && (
            <div className="space-y-3">
              {budget.length === 0 ? <div className="text-center py-12 text-muted-color">Nessun budget configurato</div> :
              budget.map(b => {
                const disponibile = b.budget_totale - b.speso - b.impegnato;
                const percSpeso = b.budget_totale > 0 ? (b.speso / b.budget_totale) * 100 : 0;
                const percImpegnato = b.budget_totale > 0 ? (b.impegnato / b.budget_totale) * 100 : 0;
                return (
                  <div key={b.id} className="bg-surface-elevated border border-border rounded-xl p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <p className="text-sm font-semibold text-white">{b.reparto}</p>
                        <p className="text-xs text-muted-color">Anno {b.anno}</p>
                      </div>
                      <div className="flex items-start gap-2">
                        <div className="text-right">
                          <p className="text-sm font-semibold text-white">€{b.budget_totale.toLocaleString('it-IT')}</p>
                          <p className={`text-xs font-semibold ${disponibile < 0 ? 'text-red-400' : 'text-green-400'}`}>€{disponibile.toLocaleString('it-IT')} disponibile</p>
                        </div>
                        <button
                          onClick={() => openEdit('budget', b)}
                          className="p-1.5 text-muted-color hover:text-sky-400 hover:bg-sky-500/10 rounded-lg transition-colors"
                          title="Modifica budget"
                        >
                          <Icon name="PencilIcon" size={15} />
                        </button>
                        <button
                          onClick={() => setDeleteConfirm({ open: true, id: b.id, name: `${b.reparto} ${b.anno}`, table: 'budget_reparti' })}
                          className="p-1.5 text-muted-color hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                          title="Elimina budget"
                        >
                          <Icon name="TrashIcon" size={15} />
                        </button>
                      </div>
                    </div>
                    <div className="w-full bg-surface rounded-full h-3 overflow-hidden">
                      <div className="h-full flex">
                        <div className="bg-red-500 h-full transition-all" style={{ width: `${Math.min(100, percSpeso)}%` }} />
                        <div className="bg-amber-500 h-full transition-all" style={{ width: `${Math.min(100 - percSpeso, percImpegnato)}%` }} />
                      </div>
                    </div>
                    <div className="flex gap-4 mt-2 text-xs">
                      <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-500 inline-block" />Speso: €{b.speso.toLocaleString('it-IT')}</span>
                      <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-500 inline-block" />Impegnato: €{b.impegnato.toLocaleString('it-IT')}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Edit Modal */}
      {showEditForm && editItem && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-surface border border-border rounded-2xl w-full max-w-lg shadow-2xl">
            <div className="flex items-center justify-between px-6 py-5 border-b border-border">
              <h2 className="text-lg font-bold text-white">
                Modifica {editItem.type === 'licenza' ? 'Licenza' : editItem.type === 'ricambio' ? 'Ricambio' : 'Budget'}
              </h2>
              <button onClick={() => setShowEditForm(false)} className="p-2 rounded-lg hover:bg-surface-elevated"><Icon name="XMarkIcon" size={18} className="text-muted-color" /></button>
            </div>
            <div className="px-6 py-5 space-y-4">
              {editItem.type === 'licenza' && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-muted-color mb-1">Nome Software *</label>
                      <input value={formLicenza.nome_software} onChange={e => setFormLicenza(p => ({ ...p, nome_software: e.target.value }))} className="w-full bg-surface-elevated border border-border rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-sky-500" />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-muted-color mb-1">Versione</label>
                      <input value={formLicenza.versione} onChange={e => setFormLicenza(p => ({ ...p, versione: e.target.value }))} className="w-full bg-surface-elevated border border-border rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-sky-500" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-muted-color mb-1">N° Licenze *</label>
                      <input type="number" min="1" value={formLicenza.numero_licenze_acquistate} onChange={e => setFormLicenza(p => ({ ...p, numero_licenze_acquistate: parseInt(e.target.value) || 1 }))} className="w-full bg-surface-elevated border border-border rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-sky-500" />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-muted-color mb-1">Data Scadenza</label>
                      <input type="date" value={formLicenza.data_scadenza} onChange={e => setFormLicenza(p => ({ ...p, data_scadenza: e.target.value }))} className="w-full bg-surface-elevated border border-border rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-sky-500" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-muted-color mb-1">Costo Totale (€)</label>
                    <input type="number" min="0" step="0.01" value={formLicenza.costo_totale} onChange={e => setFormLicenza(p => ({ ...p, costo_totale: parseFloat(e.target.value) || 0 }))} className="w-full bg-surface-elevated border border-border rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-sky-500" />
                  </div>
                </>
              )}
              {editItem.type === 'ricambio' && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-muted-color mb-1">Nome *</label>
                      <input value={formRicambio.nome} onChange={e => setFormRicambio(p => ({ ...p, nome: e.target.value }))} className="w-full bg-surface-elevated border border-border rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-sky-500" />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-muted-color mb-1">Codice</label>
                      <input value={formRicambio.codice} onChange={e => setFormRicambio(p => ({ ...p, codice: e.target.value }))} className="w-full bg-surface-elevated border border-border rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-sky-500" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-muted-color mb-1">Quantità</label>
                      <input type="number" min="0" value={formRicambio.quantita_disponibile} onChange={e => setFormRicambio(p => ({ ...p, quantita_disponibile: parseInt(e.target.value) || 0 }))} className="w-full bg-surface-elevated border border-border rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-sky-500" />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-muted-color mb-1">Costo Unitario (€)</label>
                      <input type="number" min="0" step="0.01" value={formRicambio.costo_unitario} onChange={e => setFormRicambio(p => ({ ...p, costo_unitario: parseFloat(e.target.value) || 0 }))} className="w-full bg-surface-elevated border border-border rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-sky-500" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-muted-color mb-1">Posizione</label>
                    <input value={formRicambio.posizione} onChange={e => setFormRicambio(p => ({ ...p, posizione: e.target.value }))} className="w-full bg-surface-elevated border border-border rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-sky-500" />
                  </div>
                </>
              )}
              {editItem.type === 'budget' && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-muted-color mb-1">Reparto *</label>
                      <input value={formBudget.reparto} onChange={e => setFormBudget(p => ({ ...p, reparto: e.target.value }))} className="w-full bg-surface-elevated border border-border rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-sky-500" />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-muted-color mb-1">Anno</label>
                      <input type="number" value={formBudget.anno} onChange={e => setFormBudget(p => ({ ...p, anno: parseInt(e.target.value) || new Date().getFullYear() }))} className="w-full bg-surface-elevated border border-border rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-sky-500" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-muted-color mb-1">Budget Totale (€) *</label>
                    <input type="number" min="0.01" step="0.01" value={formBudget.budget_totale} onChange={e => setFormBudget(p => ({ ...p, budget_totale: parseFloat(e.target.value) || 0 }))} className="w-full bg-surface-elevated border border-border rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-sky-500" />
                  </div>
                </>
              )}
            </div>
            <div className="flex gap-3 px-6 pb-6">
              <button onClick={() => setShowEditForm(false)} className="flex-1 py-3 rounded-xl border border-border text-sm font-semibold text-secondary-color hover:text-white hover:bg-surface-elevated transition-all">Annulla</button>
              <button onClick={handleEdit} disabled={saving} className="flex-1 py-3 rounded-xl bg-sky-500 hover:bg-sky-400 disabled:opacity-60 text-white text-sm font-bold transition-all">{saving ? 'Salvataggio...' : 'Salva Modifiche'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
